import React, { useEffect, useState } from 'react';
import { UpdateUnidadeGerencia } from '@services/UnidadeGerencia/Index.jsx';
import SweetAlert from '@components/Feedback/SweetAlert/Index.jsx';
import { goOptions, porteOptions, typesRegistrationOptions } from '@enums/UnidadeGerencia/Index.jsx';
import ButtonComp from '@components/UI/Button/Index.jsx';
import ValidationsUnidadeGerencia from '@validations/UnidadeGerencia/Validations.jsx';
import InputTextModalComp from '@components/UI/InputTextModal/Index.jsx';
import SelectOptionModalComp from '@components/UI/SelectOptionModal/Index.jsx';
import { selectMessage } from '@utils/Index.jsx';
import { getFieldProperties } from './FieldsProperties.jsx';
import { Modal } from 'antd';
import { useFetchDataGerentes } from '@hooks/api/Usuario/useFetchDataGerentes.js';
import { useFetchDataEncarregados } from '@hooks/api/Usuario/useFecthDataEncarregados.js';

function EditModal({ onUpdate, visible, closeModal, selectedRow }) {
  const [codigo, setCodigo] = useState('');
  const [tempValues, setTempValues] = useState({
    nome: '',
    sigla: '',
    gerente: null,
    encarregado: null,
    numero: '',
    tipoCadastro: null,
    go: null,
    porte: null,
  });

  const { gerentesOptions, gerenteLoading, fetchGerentes } = useFetchDataGerentes();
  const { encarregadosOptions, encarregadoLoading, fetchEncarregados } = useFetchDataEncarregados();

  useEffect(() => {
    fetchGerentes();
    fetchEncarregados();
  }, []);

  useEffect(() => {
    if (selectedRow) {
      const initialValues = {
        codigo: selectedRow.codigo,
        nome: selectedRow.nome,
        sigla: selectedRow.sigla,
        gerente: { value: selectedRow.idGerente, label: selectedRow.nomeGerente },
        encarregado: { value: selectedRow.idEncarregado, label: selectedRow.nomeEncarregado },
        numero: selectedRow.numero,
        tipoCadastro: typesRegistrationOptions.find(option => option.value === selectedRow.tipoCadastro),
        go: goOptions.find(option => option.value === selectedRow.go),
        porte: porteOptions.find(option => option.value === selectedRow.porte),
      };
      setCodigo(selectedRow.codigo);
      setTempValues(initialValues);
    }
  }, [selectedRow]);

  const checkIsDirty = () => {
    const isGerencia = tempValues.tipoCadastro?.value === "GERENCIA";

    return (
      selectedRow.nome !== tempValues.nome ||
      selectedRow.sigla !== tempValues.sigla ||
      selectedRow.idGerente !== tempValues.gerente?.value ||
      selectedRow.idEncarregado !== tempValues.encarregado?.value ||
      selectedRow.numero !== tempValues.numero ||
      selectedRow.tipoCadastro !== tempValues.tipoCadastro?.value ||
      (!isGerencia && selectedRow.go !== tempValues.go?.value) ||
      (!isGerencia && selectedRow.porte !== tempValues.porte?.value)
    );
  };

  const onSubmit = async () => {
    try {
      if (!checkIsDirty()) {
        SweetAlert.error("Erro", "Nenhuma alteração foi feita nos dados.");
        return;
      }

      const { validateFields } = ValidationsUnidadeGerencia();

      const errors = validateFields({
        ...tempValues,
        selectedOptionTypeRegistration: tempValues.tipoCadastro,
        selectedOptionGO: tempValues.go,
        selectedOptionPorte: tempValues.porte,
      });

      if (Object.keys(errors).length > 0) {
        const errorMessage = Object.values(errors).join("\n");
        SweetAlert.error("Erro na Validação", errorMessage);
        return;
      }

      const payload = {
        tipoCadastro: tempValues.tipoCadastro?.value,
        nome: tempValues.nome,
        sigla: tempValues.sigla,
        idGerente: tempValues.gerente?.value,
        idEncarregado: tempValues.encarregado?.value,
        numero: tempValues.numero,
        go: tempValues.go?.value || null,
        porte: tempValues.porte?.value || null,
      };

      SweetAlert.loading();

      const result = await UpdateUnidadeGerencia(selectedRow.id, payload);

      onUpdate({ ...result });
      closeModal();

      SweetAlert.success('Atualização Concluída!', 'Os dados foram atualizados com sucesso.');
    } catch (error) {

      const errorMessage = error.parsed?.message || 'Não foi possível atualizar a unidade/gerência.';
      
      SweetAlert.error('Erro ao atualizar', errorMessage);
      console.error(error);
    }
  };

  const handleTempChange = (field, value) => {
    setTempValues((prev) => {
      if (field === "tipoCadastro") {
        const isGerencia = value?.value === "GERENCIA";

        if (isGerencia) {
          // Quando muda para Gerência, limpar GO e Porte
          return {
            ...prev,
            [field]: value,
            go: null,
            porte: null,
          };
        } else if (value?.value === "UNIDADE") {
          // Quando muda para Unidade, manter GO e Porte nulos
          return {
            ...prev,
            [field]: value,
            go: goOptions.find(option => option.value === selectedRow.go) ?? "",
            porte: porteOptions.find(option => option.value === selectedRow.porte) ?? "",
          };
        }
      }

      // Para outros campos
      return { ...prev, [field]: value };
    });
  };

  const handleCloseWithReset = () => {
    setTempValues({
      codigo: selectedRow.codigo,
      nome: selectedRow.nome,
      sigla: selectedRow.sigla,
      gerente: { value: selectedRow.idGerente, label: selectedRow.nomeGerente },
      encarregado: { value: selectedRow.idEncarregado, label: selectedRow.nomeEncarregado },
      numero: selectedRow.numero,
      tipoCadastro: typesRegistrationOptions.find(option => option.value === selectedRow.tipoCadastro),
      go: goOptions.find(option => option.value === selectedRow.go),
      porte: porteOptions.find(option => option.value === selectedRow.porte),
    });
    closeModal();
  };

  return (
    <Modal
      getContainer={false}
      open={visible}
      onCancel={handleCloseWithReset}
      footer={null}
      width={800}
      title={<h5 className="fw-bold">Edição de Unidade/Gerência</h5>}
    >
      <hr />

      <InputTextModalComp
        label={"Código"}
        value={codigo}
        placeholder={"Código"}
        hoverMessage={"Código disponível apenas para visualização."}
        disabled
      />

      <SelectOptionModalComp
        label="Tipo de Cadastro"
        options={typesRegistrationOptions}
        value={tempValues.tipoCadastro}
        onChange={(option) => handleTempChange('tipoCadastro', option)}
        placeholder={selectMessage}
        hoverMessage={"Selecione uma Opção de Tipo de Cadastro."}
      />

      <InputTextModalComp
        label={getFieldProperties('nome', tempValues.tipoCadastro, tempValues).label}
        value={tempValues.nome}
        onChange={(e) => handleTempChange('nome', e.target.value)}
        placeholder={getFieldProperties('nome', tempValues.tipoCadastro, tempValues).placeholder}
        hoverMessage={"Preencher com o Nome da Unidade ou Gerência."}
        maxLength={255}
      />

      <InputTextModalComp
        label={getFieldProperties('sigla', tempValues.tipoCadastro, tempValues).label}
        value={tempValues.sigla}
        onChange={(e) => handleTempChange('sigla', e.target.value)}
        placeholder={getFieldProperties('sigla', tempValues.tipoCadastro, tempValues).placeholder}
        hoverMessage={"Campo desabilitado para edição."}
        maxLength={255}
        disabled
      />

      <SelectOptionModalComp
        label={"Gerente Associado"}
        options={gerentesOptions}
        isLoading={gerenteLoading}
        value={tempValues.gerente}
        onChange={(selected) => handleTempChange('gerente', selected)}
        placeholder={"Gerente Responsável"}
        hoverMessage={"Selecionar o Gerente Responsável."}
      />

      <SelectOptionModalComp
        label={"Encarregado associado"}
        options={encarregadosOptions}
        isLoading={encarregadoLoading}
        value={tempValues.encarregado}
        onChange={(selected) => handleTempChange('encarregado', selected)}
        placeholder={"Encarregado Responsável"}
        hoverMessage={"Selecionar o Encarregado Responsável."}
      />

      <InputTextModalComp
        label={getFieldProperties('numero', tempValues.tipoCadastro, tempValues).label}
        type={"number"}
        value={tempValues.numero}
        onChange={(e) => handleTempChange('numero', e.target.value)}
        placeholder={getFieldProperties('numero', tempValues.tipoCadastro, tempValues).placeholder}
        hoverMessage={"Preencher exclusivamente com caracteres númericos para Cadastro de Unidade ou Gerência."}
        maxLength={255}
        onInput={(e) => {
          e.target.value = e.target.value.replace(/[^0-9]/g, "");
          if (e.target.value.length > 2) {
            e.target.value = e.target.value.slice(0, 2); // Limita a 2 caracteres
          }
        }}
      />

      <SelectOptionModalComp
        label="GO (Unidade)"
        options={goOptions}
        value={getFieldProperties('go', tempValues.tipoCadastro, tempValues).isDisabled ? null : tempValues.go}
        onChange={(option) => handleTempChange('go', option)}
        placeholder={getFieldProperties('go', tempValues.tipoCadastro, tempValues).placeholder}
        hoverMessage={"Preenchimento Exclusivo para Cadastro de Unidades."}
        isDisabled={getFieldProperties('go', tempValues.tipoCadastro, tempValues).isDisabled}
      />

      <SelectOptionModalComp
        label={"Porte (Unidade)"}
        options={porteOptions}
        value={getFieldProperties('porte', tempValues.tipoCadastro, tempValues).isDisabled ? null : tempValues.porte}
        onChange={(option) => handleTempChange('porte', option)}
        placeholder={getFieldProperties('porte', tempValues.tipoCadastro, tempValues).placeholder}
        hoverMessage={"Preenchimento Exclusivo para Cadastro de Unidades."}
        isDisabled={getFieldProperties('porte', tempValues.tipoCadastro, tempValues).isDisabled}
      />

      <div className="d-flex justify-content-end gap-2 mt-4">
        <ButtonComp
          title="ATUALIZAR"
          buttonColor="#2b97be"
          classnameIcon="fa-regular fa-floppy-disk"
          onClick={onSubmit}
          hoverMessage={"Botão para confirmar a atualização"}
        />

        <ButtonComp
          title="CANCELAR"
          buttonColor="#dc3545"
          classnameIcon="fa-regular fa-circle-xmark"
          onClick={handleCloseWithReset}
          hoverMessage={"Botão para cancelar a atualização"}
        />
      </div>

    </Modal>
  );
};

export default EditModal;
